import React, { PureComponent } from 'react';
import {
  SafeAreaView,
  StyleSheet, View, Text,
  Image,
  AppState,
  AppStateStatus,
} from 'react-native';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { defaultScale } from '../../../../utils/scaling';
import { getNextStep, navigateToNextStep } from '../../../actions';
import { IAppStore } from '../../../../HomeWorkouts';
import { GoogleAnalytics } from '../../../../services/google-analytics';
import Theme from '../../../../theme';
import { UserDataConsumer } from '../../../../utils';
import HealthConnectImage from '../../../../assets/icons/health_connect_logo.png';
import { IConnectedAppOnboardingProps, navigateToNextOnboardingStep } from '../ConnectedAppEntryPointScreen';
import SupportedDevices from '../../../../assets/icons/connected-apps/supported-devices.svg';
import FooterButtons from '../shared/FooterButtons';
import { connectToApp, handleHealthConnectInstallation } from '../utils';
import { showMessage } from '../../../../ui/components/Toast';
import ConnectedAppsManagerSingleton, { IAppForOnboarding } from '../../../../ConnectedApps';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingVertical: defaultScale(16),
    backgroundColor: Theme.WHITE,
  },
  headerContainer: {
    alignItems: 'center',
    marginTop: '10%',
    textAlign: 'center',
    flex: 1,
  },
  heading: {
    textAlign: 'center',
  },
  subHeader: {
    color: Theme.GREY_DARK_MEDIUM,
    marginBottom: defaultScale(20),
    paddingHorizontal: defaultScale(10),
    textAlign: 'center',
  },
});

interface IState {
  isLoading: boolean;
  openedPlayStore: boolean;
}

class InstallHealthConnect extends PureComponent<IConnectedAppOnboardingProps & WithTranslation, IState> {
  private readonly googleAnalytics: GoogleAnalytics;

  private readonly appForOnboarding: IAppForOnboarding = this.props.navigation.getParam('appForOnboarding');

  public constructor(props: IConnectedAppOnboardingProps & WithTranslation) {
    super(props);
    this.googleAnalytics = new GoogleAnalytics();
    this.state = {
      isLoading: false,
      openedPlayStore: false,
    };
  }

  public handleAppStateChange = (nextAppState: AppStateStatus) => {
    if (nextAppState === 'active' && this.state.openedPlayStore) {
      this.requestAuth().catch(() => {
        this.setState({ isLoading: false });
      });
    }
  };

  public componentDidMount() {
    AppState.addEventListener(
      'change',
      this.handleAppStateChange,
    );
  }

  public componentWillUnmount() {
    AppState.removeEventListener(
      'change',
      this.handleAppStateChange,
    );
  }

  public render() {
    const { t } = this.props;

    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.headerContainer}>
          <Text style={[Theme.textStyle.heading, styles.heading]}>{t('installHealthConnectApp')}</Text>
          <Image
            source={HealthConnectImage}
            style={{ width: defaultScale(100), height: defaultScale(100), marginTop: defaultScale(30) }}
          />
          <Text style={[Theme.textStyle.bodyCopyReg, styles.subHeader]}>
            {t('installGoogleHealthConnectAppDescription')}
          </Text>
          <SupportedDevices width={defaultScale(300)} />
        </View>
        <FooterButtons
          primaryButtonText={t('installOnThePlayStore')}
          secondaryButtonText={t('appOnboardingSkip')}
          onPrimaryButtonPress={() => this.requestAuth()}
          onSecondaryButtonPress={() => this.proceedToNextStep()}
          isLoading={this.state.isLoading}
        />
      </SafeAreaView>
    );
  }

  private async requestAuth() {
    this.setState({ isLoading: true });

    try {
      const app = ConnectedAppsManagerSingleton.getAppByName(this.appForOnboarding.app);
      if (!app) {
        showMessage(`${this.appForOnboarding.app} failed to install`);
        return;
      }

      const autoInstallApp = !this.state.openedPlayStore;
      const installationResult = await handleHealthConnectInstallation(autoInstallApp);

      if (installationResult === 'loading') {
        this.setState({ openedPlayStore: true });
      } else if (installationResult === 'error') {
        this.setState({ isLoading: false, openedPlayStore: false });
        return;
      }

      if (installationResult === 'success') {
        // clear openedPlayStore to handle case where app is installed and only partial permissions are granted.
        this.setState({ openedPlayStore: false });

        const result = await connectToApp(app);

        if (result === 'success') {
          this.googleAnalytics.logEventWithParamaters('sign_up_install_health_connect', { enabled: true });
          this.props.navigation.navigate('DoYouHaveATrackingDevice', { appForOnboarding: this.appForOnboarding });
        }
        this.setState({ isLoading: false });
      }
    } catch (error) {
      this.setState({ isLoading: false, openedPlayStore: false });
      showMessage(`${this.appForOnboarding.app} failed`);
    }
  }

  private proceedToNextStep = () => {
    navigateToNextOnboardingStep(
      this.props,
      this.props.getNextStep,
      this.props.navigateToNextStep,
    );
    this.googleAnalytics.logEventWithParamaters('sign_up_install_health_connect_proceed', { enabled: true });
  };
}

const mapStateToProps = (state: IAppStore) => ({
  enabledFeatures: state.auth.signup.enabledFeatures,
});

const mapDispatchToProps = {
  getNextStep,
  navigateToNextStep,
};

export default withTranslation(['auth', 'settings'])(
  connect(mapStateToProps, mapDispatchToProps)(UserDataConsumer(InstallHealthConnect)),
);
